import { buildNotePreview, getNoteAccentColor } from "../utils/myNotes";

const isApiEnabled = () => {
  return true;
};

const getNotesBaseUrl = () => `${process.env.REACT_APP_API_URL}notes`;

export const getNoteUserId = () => localStorage.getItem("user_id") || "";

const deriveSectionLabel = (updatedAt) => {
  if (!updatedAt) {
    return "Other";
  }

  const date = new Date(updatedAt);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfNoteDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayDiff = Math.round((startOfToday - startOfNoteDay) / (24 * 60 * 60 * 1000));

  if (dayDiff === 0) {
    return "Today";
  }
  if (dayDiff === 1) {
    return "Yesterday";
  }

  if (date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth()) {
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }

  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString("en-GB", {
      month: "long",
      year: "numeric",
    });
  }

  return String(date.getFullYear());
};

const normalizeNote = (note) => ({
  ...note,
  tags: [...(note.tags || [])],
  attachments: [...(note.attachments || [])],
  sectionLabel: note.sectionLabel || deriveSectionLabel(note.updatedAt),
  preview: buildNotePreview(note),
  accentColor: note.accentColor || getNoteAccentColor(note),
});

const getPendingFiles = (attachments = []) =>
  attachments
    .filter((attachment) => attachment.file instanceof File)
    .map((attachment) => attachment.file);

const makeAuthenticatedFormRequest = async (url, formData, method = "POST") => {
  let token = localStorage.getItem("access_token");
  const authContext = window.authContext;

  if (!token) {
    throw new Error("Not authenticated");
  }

  const sendRequest = async (authToken) => {
    const response = await fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      body: formData,
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("UNAUTHORIZED");
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response;
  };

  try {
    return await sendRequest(token);
  } catch (error) {
    if (error.message === "UNAUTHORIZED" && authContext?.refreshToken) {
      const newToken = await authContext.refreshToken();
      return sendRequest(newToken);
    }
    throw error;
  }
};

const appendNoteFieldsToFormData = (formData, noteDraft, { isUpdate = false } = {}) => {
  formData.append("userId", getNoteUserId());
  formData.append("title", noteDraft.title);
  formData.append("content", noteDraft.content);
  formData.append("tags", JSON.stringify(noteDraft.tags || []));

  if (isUpdate) {
    const idsToRemove = noteDraft.attachmentIdsToRemove || [];
    formData.append(
      "attachmentIdsToRemove",
      idsToRemove.length ? JSON.stringify(idsToRemove) : "",
    );
  }
};

const saveNoteWithApi = async ({ noteId, noteDraft }) => {
  const url = noteId ? `${getNotesBaseUrl()}/${noteId}` : getNotesBaseUrl();
  const method = noteId ? "PUT" : "POST";
  const formData = new FormData();

  appendNoteFieldsToFormData(formData, noteDraft, { isUpdate: Boolean(noteId) });

  getPendingFiles(noteDraft.attachments).forEach((file) => {
    formData.append("files", file);
  });

  const response = await makeAuthenticatedFormRequest(url, formData, method);
  return response.json();
};

export const getMyNotes = async () => {
  if (!isApiEnabled()) {
    throw new Error("My Notes API is disabled.");
  }

  const { makeAuthenticatedRequest } = await import("../utils/tokenUtils");
  const userId = getNoteUserId();
  const query = userId ? `?userId=${encodeURIComponent(userId)}` : "";
  const response = await makeAuthenticatedRequest(`${getNotesBaseUrl()}/all${query}`, {
    method: "GET",
  });
  const payload = await response.json();
  const notes = Array.isArray(payload) ? payload : payload.notes || [];
  return notes.map(normalizeNote);
};

export const searchMyNotes = async (searchQuery = "") => {
  if (!isApiEnabled()) {
    throw new Error("My Notes API is disabled.");
  }

  const trimmedQuery = String(searchQuery).trim();
  if (!trimmedQuery) {
    return [];
  }

  const { makeAuthenticatedRequest } = await import("../utils/tokenUtils");
  const userId = getNoteUserId();
  const params = new URLSearchParams();
  if (userId) {
    params.set("userId", userId);
  }
  params.set("q", trimmedQuery);

  const response = await makeAuthenticatedRequest(
    `${getNotesBaseUrl()}/search?${params.toString()}`,
    { method: "GET" },
  );
  const payload = await response.json();
  const notes = Array.isArray(payload?.notes) ? payload.notes : [];
  return notes.map(normalizeNote);
};

export const updateMyNote = async (noteId, updates) => {
  if (!isApiEnabled()) {
    throw new Error("My Notes API is disabled.");
  }

  const updatedNote = await saveNoteWithApi({ noteId, noteDraft: updates });
  return normalizeNote(updatedNote);
};

export const createMyNote = async (noteDraft) => {
  if (!isApiEnabled()) {
    throw new Error("My Notes API is disabled.");
  }

  const createdNote = await saveNoteWithApi({ noteDraft });
  return normalizeNote(createdNote);
};

export const getMyNotesSubjects = async () => {
  if (!isApiEnabled()) {
    throw new Error("My Notes API is disabled.");
  }

  const { makeAuthenticatedRequest } = await import("../utils/tokenUtils");
  const response = await makeAuthenticatedRequest(
    `${getNotesBaseUrl()}/subjects`,
    {
      method: "GET",
    }
  );

  const payload = await response.json();
  const subjects = Array.isArray(payload?.subjects) ? payload.subjects : [];

  const formattedSubjects = subjects
    .filter((subject) => subject?.name)
    .map((subject) => ({
      id: subject.id,
      name: String(subject.name).trim(),
    }))
    .sort((left, right) => left.name.localeCompare(right.name));

  const hasEssay = formattedSubjects.some(
    (subject) => subject.name.toLowerCase() === "essay"
  );

  if (!hasEssay) {
    formattedSubjects.push({
      id: "essay",
      name: "Essay",
    });
  }

  return formattedSubjects;
};

export const getMyNotesTopics = async (subjectIds = []) => {
  if (!isApiEnabled()) {
    throw new Error("My Notes API is disabled.");
  }

  const numericIds = subjectIds
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0);

  if (!numericIds.length) {
    return { groups: [], topicNames: [] };
  }

  const params = new URLSearchParams();
  numericIds.forEach((id) => {
    params.append("subjectIds", String(id));
  });

  const { makeAuthenticatedRequest } = await import("../utils/tokenUtils");
  const response = await makeAuthenticatedRequest(
    `${getNotesBaseUrl()}/topics?${params.toString()}`,
    { method: "GET" },
  );
  const payload = await response.json();
  const groups = Array.isArray(payload?.data) ? payload.data : [];
  const topicNames = [];
  const seen = new Set();

  groups.forEach((group) => {
    (group?.topics || []).forEach((topic) => {
      const name = String(topic?.name || "").trim();
      if (!name) {
        return;
      }
      const key = name.toLowerCase();
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      topicNames.push(name);
    });
  });

  topicNames.sort((left, right) => left.localeCompare(right));

  return { groups, topicNames };
};

export const getFilteredNotesBySubjects = async (subjects = []) => {
  if (!isApiEnabled()) {
    throw new Error("My Notes API is disabled.");
  }

  const { makeAuthenticatedRequest } = await import("../utils/tokenUtils");
  const userId = getNoteUserId();

  const params = new URLSearchParams();
  if (userId) {
    params.set("userId", userId);
  }
  subjects.forEach((subject) => {
    params.append("subject", subject);
  });

  const response = await makeAuthenticatedRequest(
    `${getNotesBaseUrl()}/all?${params.toString()}`,
    { method: "GET" },
  );
  const payload = await response.json();
  const notes = Array.isArray(payload) ? payload : payload.notes || [];
  return notes.map(normalizeNote);
};

export const deleteMyNote = async (noteId) => {
  if (!isApiEnabled()) {
    throw new Error("My Notes API is disabled.");
  }

  const { makeAuthenticatedRequest } = await import("../utils/tokenUtils");
  await makeAuthenticatedRequest(`${getNotesBaseUrl()}/${noteId}?json=true`, {
    method: "DELETE",
  });
  return true;
};
